import * as React from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useQuery } from '@tanstack/react-query';
import { InformationCircleIcon, CreditCardIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAccountsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { toast } from 'react-toastify';
import { ButtonV2, IconButton, Title, Link } from '@restworld/ui-ds';
import { useKv } from '@restworld/utils-common';
import { useSetAtom } from 'jotai';
import PaymentSuccessModal from './payment-success-modal';
import ConsumptionDetailModal from './consumption-detail-modal';
import { consumptionDetailModalOpenAtom } from './consumption-detail-modal/atoms';

const NextPaymentDetails: React.FunctionComponent = () => {
  const { t } = useTranslation('payment-and-invoices');
  const accountsService = useAccountsService();
  const { user } = useAuth();
  const { setKV, getKV } = useKv();
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [shouldCheckPayment, setShouldCheckPayment] = React.useState(false);
  const setConsumptionDetailOpen = useSetAtom(consumptionDetailModalOpenAtom);

  const pendingPaymentKey = React.useMemo(
    () => `pending_payment_check_${user?.organization_id}`,
    [user?.organization_id]
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['nextPaymentDetails'],
    queryFn: async () => {
      const response = await accountsService.getNextPaymentDetails();
      return response.data;
    },
    retry: 5,
    retryDelay: 1000,
  });

  React.useEffect(() => {
    if (isError) {
      toast.error(t('next_payment_details_error'));
    }
  }, [isError, t]);

  // Check KV store on mount and when page becomes visible
  React.useEffect(() => {
    const checkPendingPayment = async () => {
      if (!user?.organization_id) return;

      const kvData = await getKV(pendingPaymentKey);
      if (kvData?.value === 'true') {
        setShouldCheckPayment(true);
      }
    };

    // Check on mount
    checkPendingPayment();

    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPendingPayment();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.organization_id, getKV, pendingPaymentKey]);

  // When shouldCheckPayment is true, refetch and check if payment is cleared
  React.useEffect(() => {
    if (shouldCheckPayment && !isLoading) {
      refetch().then(async ({ data: refetchedData }) => {
        const isUnpaid = refetchedData?.type === 'unpaid';

        if (!isUnpaid) {
          // Payment is cleared (moved to upcoming or no data), show success modal
          setShowSuccessModal(true);
          // Clear KV store
          await setKV({ key: pendingPaymentKey, value: 'false' });
        }
        setShouldCheckPayment(false);
      });
    }
  }, [shouldCheckPayment, isLoading, refetch, setKV, pendingPaymentKey]);

  const handlePayInvoice = React.useCallback(async () => {
    if (data?.hosted_url && user?.organization_id) {
      // Store pending payment check in KV store
      await setKV({ key: pendingPaymentKey, value: 'true' });
      window.open(data.hosted_url, '_blank', 'noopener,noreferrer');
    }
  }, [data?.hosted_url, user?.organization_id, setKV, pendingPaymentKey]);

  const handleCloseSuccessModal = React.useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  if (isLoading) {
    return (
      <Section>
        <SectionHeader>
          <TitleContainer>
            <Title fontSize="large" fontWeight="semibold" textColor="neutral-700" content={t('next_payment_title')} />
            <IconButton Icon={InformationCircleIcon} ariaLabel="Payment information" variant="neutral" size="sm" />
          </TitleContainer>
        </SectionHeader>
        <ContentCard>
          <div tw="flex items-center justify-center py-8">
            <div tw="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        </ContentCard>
      </Section>
    );
  }

  if (isError || !data) {
    return null;
  }

  const isUnpaidInvoice = data.type === 'unpaid';
  const hasCreditsApplied = data.credit_days_used > 0 && data.credit_discount_amount > 0;

  return (
    <>
      <Section>
        <SectionHeader>
          <TitleContainer>
            <Title fontSize="large" fontWeight="semibold" textColor="neutral-700" content={t('next_payment_title')} />
          </TitleContainer>
        </SectionHeader>
        <ContentCard>
          <DataCardsContainer>
            <DataCard>
              <DataCardHeader>
                <DataValue>{data.units_consumed ?? 0}</DataValue>
                {hasCreditsApplied && (
                  <CreditBadge>{t('credit_days_badge', { count: data.credit_days_used })}</CreditBadge>
                )}
              </DataCardHeader>
              <DataLabel>{t('units_consumed')}</DataLabel>
            </DataCard>
            <DataCard>
              <DataCardHeader>
                {hasCreditsApplied && (
                  <OriginalAmount>€{data.amount_before_credits?.toFixed(2) ?? '0.00'}</OriginalAmount>
                )}
                <DataValue>
                  €{(hasCreditsApplied ? data.amount_after_credits : data.amount_to_pay)?.toFixed(2) ?? '0.00'}
                </DataValue>
                {hasCreditsApplied && (
                  <CreditBadge>
                    {t('credit_discount_badge', { amount: data.credit_discount_amount.toFixed(2) })}
                  </CreditBadge>
                )}
              </DataCardHeader>
              <DataLabel>
                {data?.tax && data?.tax > 0
                  ? t('amount_to_pay_with_vat', { vat: data?.tax?.toFixed(2) ?? '0.00' })
                  : t('amount_to_pay')}
              </DataLabel>
            </DataCard>
            <DataCard>
              <DataValue>{data.due_date ?? '-'}</DataValue>
              <DataLabel>{t('due_date_label')}</DataLabel>
            </DataCard>
          </DataCardsContainer>
          <Link StartIcon={PlusIcon} newTab={false} onClick={() => setConsumptionDetailOpen(true)}>
            {t('more_details')}
          </Link>
          {isUnpaidInvoice && (
            <ButtonV2
              variant="secondary-danger"
              StartIcon={CreditCardIcon}
              onClick={handlePayInvoice}
              disabled={!data.hosted_url}
              fullwidth
              twStyle={tw`h-12`}
            >
              {t('pay_invoice')}
            </ButtonV2>
          )}
        </ContentCard>
      </Section>
      <ConsumptionDetailModal />
      <PaymentSuccessModal isOpen={showSuccessModal} onClose={handleCloseSuccessModal} />
    </>
  );
};

export default NextPaymentDetails;

const Section = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const SectionHeader = styled.div`
  ${tw`flex h-10 items-center justify-between`}
`;

const TitleContainer = styled.div`
  ${tw`flex gap-2 items-center w-full`}
`;

const ContentCard = styled.div`
  ${tw`flex flex-col gap-4 items-center justify-end p-4 rounded-2xl shadow-sm border border-[#dedee1]`}
  background: #fafafa;
`;

const DataCardsContainer = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
  ${tw`md:flex-row`}
`;

const DataCard = styled.div`
  ${tw`flex-1 flex flex-col gap-1 items-start justify-end p-4 rounded-lg`}
  background: #f5f5f6;
`;

const DataValue = styled.div`
  ${tw`flex gap-2 items-center font-semibold text-xl text-neutral-900`}
`;

const DataLabel = styled.p`
  ${tw`font-medium text-sm leading-normal`}
  color: #1c1c33;
`;

const DataCardHeader = styled.div`
  ${tw`flex flex-wrap gap-2 items-center`}
`;

const CreditBadge = styled.span`
  ${tw`inline-flex items-center justify-center gap-1 px-2 font-medium text-sm rounded`}
  height: 24px;
  background: #e6f1ec;
  border: 1px solid #00542d;
  color: #00542d;
`;

const OriginalAmount = styled.span`
  ${tw`font-semibold text-xl line-through`}
  color: #757583;
`;
